import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "If that email is registered, a reset link is on the way.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 grid lg:grid-cols-5">
      <div className="lg:col-span-3 flex flex-col p-8 lg:p-16">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-50 transition-colors w-fit" data-testid="back-to-login-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="flex-1 flex items-center max-w-md w-full mx-auto">
          <div className="w-full">
            <div className="mb-10">
              <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Forgot your password?</h1>
              <p className="text-slate-400">
                Enter your email and we&apos;ll send a link to set a new one. The link expires in 30 minutes.
              </p>
            </div>

            {sent ? (
              <div data-testid="forgot-password-sent-state" className="rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-6">
                <MailCheck className="w-8 h-8 text-cyan-400 mb-3" />
                <div className="font-semibold text-slate-50 mb-1">Check your inbox</div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  If <span className="text-slate-200">{email}</span> is registered, we&apos;ve just sent a reset link.
                  It expires in 30 minutes. Don&apos;t see it? Check spam, or{" "}
                  <button
                    type="button"
                    data-testid="forgot-password-resend-button"
                    onClick={() => setSent(false)}
                    className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                  >
                    try again with a different address
                  </button>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Email</label>
                  <input
                    data-testid="forgot-password-email-input"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
                <button
                  data-testid="forgot-password-submit-button"
                  type="submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
                >
                  {busy ? "Sending…" : "Send reset link"} {!busy && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            )}

            <div className="mt-8 text-sm text-slate-400">
              Remembered it?{" "}
              <Link to="/login" data-testid="forgot-password-to-login-link" className="text-cyan-400 hover:text-cyan-300 font-medium">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden bg-[#040814]">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div>
            <img src="/brand/steele-cloud-long.png" alt="Steele Cloud" className="w-full max-w-[320px] mb-10 -ml-2" />
            <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
              A secure door.<br/>Even when you<br/>lose the key.
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
